import { useQuery } from '@tanstack/react-query'
import { fetchCatalog, fetchTags, fetchCVEs, fetchStats, enrichImageData } from '@/lib/api'
import type { Image } from '@/types'

export function useCatalog() {
  return useQuery({
    queryKey: ['catalog'],
    queryFn: fetchCatalog,
  })
}

export function useTags(name: string) {
  return useQuery({
    queryKey: ['tags', name],
    queryFn: () => fetchTags(name),
    enabled: !!name,
  })
}

export function useCVEs(name: string, tag?: string) {
  return useQuery({
    queryKey: ['cves', name, tag],
    queryFn: () => fetchCVEs(name, tag),
    enabled: !!name,
  })
}

export function useStats() {
  return useQuery({
    queryKey: ['stats'],
    queryFn: fetchStats,
  })
}

export function useImages() {
  const { data: catalog, isLoading, error } = useCatalog()

  const images: Image[] = (catalog?.repositories || []).map(name => {
    const metadata = enrichImageData(name)
    return {
      name,
      description: metadata.description,
      category: metadata.category || 'base',
      tags: [],
      cveCount: 0,
      lastUpdated: new Date().toISOString(),
      verified: true,
    }
  })

  return { images, isLoading, error }
}

export function useFilteredImages(category?: string, search?: string) {
  const { images, isLoading, error } = useImages()

  const filtered = images.filter(img => {
    if (category && category !== 'all' && img.category !== category) return false
    if (search && !img.name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  return { images: filtered, isLoading, error }
}
